import { useEffect, useState, type FormEvent } from "react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
} from "@diametral/design-system/react";

import { useAssign, useEndAssignment } from "../../api/assignments";
import { useSetProjectRoles, useSetProjectStatus } from "../../api/projects";
import type { PersonBrief, PickerPerson, ProjectDetail, ProjectStatus, Stream, StreamMember } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PeopleListField, PersonSelect } from "../../components/PersonPicker";
import { SelectField } from "../../components/SelectField";
import { formatDate } from "../../lib/format";
import { useTranslation } from "../../lib/i18n";
import { notifySuccess } from "../../lib/toast";
import { todayIso } from "../../lib/week";

/** Pause, reactivate or close the project (DAR). Closing is irreversible: confirmed first. */
export function StatusDialog({
  target,
  onClose,
  project,
}: {
  target: ProjectStatus | null;
  onClose: () => void;
  project: ProjectDetail;
}) {
  const { t } = useTranslation();
  const setStatus = useSetProjectStatus(project.slug);
  const copy: Record<ProjectStatus, { title: string; description: string; confirm: string }> = {
    paused: {
      title: t("Pause “{{title}}”?", { title: project.title }),
      description: t("The project no longer accepts requests to join it. Assignments carry on and reports remain due."),
      confirm: t("Pause"),
    },
    active: {
      title: t("Reactivate “{{title}}”?", { title: project.title }),
      description: t("The project accepts requests to join it again."),
      confirm: t("Reactivate the project"),
    },
    closed: {
      title: t("Close “{{title}}”?", { title: project.title }),
      description: t(
        "Every assignment ends today (upcoming ones are deleted), pending requests are cancelled and the people concerned are notified by email. A closed project cannot be reopened.",
      ),
      confirm: t("Close permanently"),
    },
  };
  const text = target ? copy[target] : null;
  return (
    <ConfirmDialog
      open={target !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={text?.title ?? ""}
      description={text?.description ?? ""}
      confirmLabel={text?.confirm ?? t("Confirm")}
      danger={target === "closed"}
      pending={setStatus.isPending}
      onConfirm={() => {
        if (!target) return;
        setStatus.mutate(target, {
          onSuccess: (result) => {
            onClose();
            if (result.status === "closed") {
              notifySuccess(
                t("Project closed"),
                t("{{assignments}}, {{requests}}.", {
                  assignments: t("{{count}} assignments ended", { count: result.ended_assignments }),
                  requests: t("{{count}} requests cancelled", { count: result.cancelled_requests }),
                }),
              );
            } else {
              notifySuccess(result.status === "paused" ? t("Project paused") : t("Project reactivated"));
            }
          },
        });
      }}
    />
  );
}

/** Name the tech leads and product owners (DAR). */
export function RolesDialog({
  open,
  onOpenChange,
  project,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
}) {
  const { t } = useTranslation();
  const [techLeads, setTechLeads] = useState<PersonBrief[]>(project.tech_leads);
  const [owners, setOwners] = useState<PersonBrief[]>(project.product_owners);
  const save = useSetProjectRoles(project.slug);

  useEffect(() => {
    if (open) {
      setTechLeads(project.tech_leads);
      setOwners(project.product_owners);
    }
    // Reset on open only: a background refetch of the project must not wipe the form.
  }, [open]);

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(
      { tech_leads: techLeads.map((p) => p.id), product_owners: owners.map((p) => p.id) },
      {
        onSuccess: () => {
          notifySuccess(t("TL/PO saved"));
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => onOpenChange(next)}>
      <DialogContent className="ada-dialog ada-dialog--wide">
        <DialogHeader>
          <DialogTitle>{t("Tech leads and product owners")}</DialogTitle>
          <DialogDescription>
            {t("They edit the project's overview, streams and repositories, and are copied on requests.")}
          </DialogDescription>
        </DialogHeader>
        <form className="ada-form" onSubmit={submit} noValidate>
          <Field>
            <FieldLabel htmlFor="roles-tl">{t("Tech leads")}</FieldLabel>
            <PeopleListField id="roles-tl" label={t("Tech leads")} value={techLeads} onChange={setTechLeads} />
          </Field>
          <Field>
            <FieldLabel htmlFor="roles-po">{t("Product owners")}</FieldLabel>
            <PeopleListField id="roles-po" label={t("Product owners")} value={owners} onChange={setOwners} />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>{t("Cancel")}</DialogClose>
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t("Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Assign an (assignable) person to a stream directly, without a request (DAR). */
export function AssignDialog({
  open,
  onOpenChange,
  project,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
}) {
  const { t } = useTranslation();
  const streams = project.streams.filter((s) => !s.archived);
  const [person, setPerson] = useState<PickerPerson | null>(null);
  const [streamId, setStreamId] = useState("");
  const [start, setStart] = useState(todayIso());
  const assign = useAssign();

  useEffect(() => {
    if (open) {
      setPerson(null);
      setStreamId(streams.length === 1 ? String(streams[0]?.id) : "");
      setStart(todayIso());
    }
    // Reset on open only: a background refetch of the project must not wipe the form.
  }, [open]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!person || !streamId) return;
    assign.mutate(
      { person: person.id, stream_id: Number(streamId), start_date: start || null },
      {
        onSuccess: (assignment) => {
          notifySuccess(
            t("Assignment created"),
            t("{{name}} → {{stream}}, from {{date}}.", {
              name: assignment.person.name,
              stream: assignment.stream,
              date: formatDate(assignment.start_date),
            }),
          );
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => onOpenChange(next)}>
      <DialogContent className="ada-dialog">
        <DialogHeader>
          <DialogTitle>{t("Assign a person")}</DialogTitle>
          <DialogDescription>
            {t("Direct assignment to a stream of {{title}}. The person and the TL/PO are notified by email.", { title: project.title })}
          </DialogDescription>
        </DialogHeader>
        <form className="ada-form" onSubmit={submit} noValidate>
          <Field>
            <FieldLabel htmlFor="assign-person">{t("Person")}</FieldLabel>
            <PersonSelect id="assign-person" label={t("Person to assign")} assignable value={person} onChange={setPerson} />
            <FieldDescription>{t("Only assignable people (DAR rule) are offered.")}</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="assign-stream">{t("Stream")}</FieldLabel>
            <SelectField
              id="assign-stream"
              label={t("Stream")}
              placeholder={t("Choose a stream")}
              value={streamId}
              onChange={setStreamId}
              options={streams.map((s) => ({ value: String(s.id), label: s.title }))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="assign-start">{t("Start date")}</FieldLabel>
            <Input id="assign-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>{t("Cancel")}</DialogClose>
            <Button type="submit" variant="primary" loading={assign.isPending} disabled={!person || !streamId}>
              {t("Assign")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** End (or cancel, if it has not started) one assignment (DAR). */
export function EndAssignmentDialog({
  target,
  onClose,
}: {
  target: { member: StreamMember; stream: Stream } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const endAssignment = useEndAssignment();

  useEffect(() => {
    if (target) setEnd(todayIso());
  }, [target]);

  const member = target?.member;
  const before = member && end && end < member.start_date;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!member) return;
    endAssignment.mutate(
      { id: member.assignment_id, end_date: end || null },
      {
        onSuccess: (result) => {
          notifySuccess(
            result.outcome === "deleted" ? t("Assignment deleted") : t("Assignment finished"),
            result.outcome === "deleted"
              ? t("{{name}}: the assignment had not started.", { name: result.person.name })
              : t("{{name}} leaves {{stream}} on {{date}}.", {
                  name: result.person.name,
                  stream: result.stream,
                  date: formatDate(result.end_date),
                }),
          );
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={target !== null} onOpenChange={(open: boolean) => (open ? undefined : onClose())}>
      <DialogContent className="ada-dialog">
        <DialogHeader>
          <DialogTitle>{member?.upcoming ? t("Cancel the assignment") : t("End the assignment")}</DialogTitle>
          <DialogDescription>
            {member
              ? t("{{name}} · stream “{{stream}}”, since {{date}}.", {
                  name: member.name,
                  stream: target?.stream.title,
                  date: formatDate(member.start_date),
                })
              : ""}
          </DialogDescription>
        </DialogHeader>
        <form className="ada-form" onSubmit={submit} noValidate>
          <Field>
            <FieldLabel htmlFor="end-date">{t("Last day")}</FieldLabel>
            <Input id="end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            <FieldDescription>
              {before
                ? t("Date before the start: the assignment will be deleted.")
                : t("Included. The person and the TL/PO are notified by email.")}
            </FieldDescription>
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>{t("Cancel")}</DialogClose>
            <Button type="submit" variant="danger" loading={endAssignment.isPending}>
              {before ? t("Delete the assignment") : t("End the assignment")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
